import React, { useEffect } from 'react'
import { useTracking } from 'react-tracking'
import ReactMarkdown from 'markdown-to-jsx'

import { Button } from '@ynap/button'
import BEMHelper from '@ynap/bem-helper'
import EmptyList from '@ynap/empty-list'
import { Notification } from '@ynap/notification'

import OrdersSection from './OrdersSection'
import { myaccountPageData } from '../../analytics'
import type { OrderHistoryProps } from './types'

const bem = new BEMHelper('OrderHistory')

const OrderHistory = (props: OrderHistoryProps) => {
    const {
        className,
        delayMessage,
        exchangesNotAllowed,
        fetchOrderHistory,
        fetchOrderHistoryError,
        isFetchingOrderHistory,
        messages,
        noOrdersLink,
        orders,
        nativeApp,
        pageSize,
        returnWindow,
        showExchangeDerivedOrderIds,
        showTitle = true,
    } = props
    const { recent, past } = orders
    const { trackEvent } = useTracking()

    useEffect(() => {
        trackEvent(
            myaccountPageData({
                name: 'my orders',
                subCategory1: 'my orders',
            })(props)
        )
    }, [])

    // If some error occured, show user a message and a button so he can retry fetching order history requests
    const hasError = !(recent || past) || (fetchOrderHistoryError.recent && fetchOrderHistoryError.past)
    if (hasError) {
        return (
            <div className={bem(null, null, className)}>
                <EmptyList
                    className={bem('empty', nativeApp ? 'nativeApp' : '')}
                    variant="myAccountError"
                    title={messages.orderHistory.pageError()}
                >
                    <Button
                        type="primary-link"
                        className={bem('emptyLink')}
                        onClick={() => {
                            fetchOrderHistory(1, pageSize, 'recent')
                            fetchOrderHistory(1, pageSize, 'past')
                        }}
                    >
                        {messages.orderHistory.retryButton()}
                    </Button>
                </EmptyList>
            </div>
        )
    }

    // If customer has no orders, show empty state including a button to prompt user to shop latest arrivals
    if (recent?.items?.length === 0 && past?.items?.length === 0) {
        return (
            <div className={bem(null, null, className)}>
                <EmptyList
                    className={bem('empty', nativeApp ? 'nativeApp' : '')}
                    icon={<div className={bem('noOrdersIcon')} />}
                    title={messages.orderHistory.noOrdersTitle()}
                    details={messages.orderHistory.noOrdersDescription()}
                >
                    <Button component="a" href={noOrdersLink} type="primary-link" className={bem('emptyLink')}>
                        {messages.orderHistory.noOrdersLinkTitle()}
                    </Button>
                </EmptyList>
            </div>
        )
    }

    const { description, refundsOnlyDescription, canBeRefundedNotExchanged, canBeRefunded } = messages.orderHistory
    const headingDescription = exchangesNotAllowed && refundsOnlyDescription ? refundsOnlyDescription() : description()
    const recentOrdersDescription =
        exchangesNotAllowed && canBeRefundedNotExchanged ? canBeRefundedNotExchanged() : canBeRefunded()
    const DelayComponent = (props) => <ReactMarkdown {...props}>{delayMessage}</ReactMarkdown>

    const commonProps = {
        fetchOrderHistory,
        fetchOrderHistoryError,
        showExchangeDerivedOrderIds,
        isFetchingOrderHistory,
        messages,
        pageSize,
    }
    return (
        <div className={bem(null, null, className)}>
            <div className={bem('heading')}>
                {showTitle ? <div className={bem('title')}>{messages.orderHistory.title()}</div> : null}
                <div className={bem('description')}>{headingDescription}</div>
                {messages.orderHistory.terms && (
                    <div className={bem('description')}>
                        {messages.orderHistory.terms({
                            returnWindow,
                        })}
                    </div>
                )}
                {delayMessage && (
                    <Notification className={bem('orderDelay')} component={DelayComponent} type="info" message="" />
                )}
            </div>
            <OrdersSection
                {...commonProps}
                orders={orders['recent']}
                category="recent"
                description={recentOrdersDescription}
            />
            <OrdersSection
                {...commonProps}
                orders={orders['past']}
                category="past"
                description={messages.orderHistory.cannotBeRefunded()}
            />
        </div>
    )
}

export default OrderHistory
